import React, { useState, useEffect } from "react";
import PageWrapper from "../components/PageWrapper";
import "./css/Contact.css";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { Helmet } from "react-helmet";

const Contact = ({ user }) => {
  const [formData, setFormData] = useState({
    name: user?.displayName || "",
    email: user?.email || "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [isSending, setIsSending] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // If user is logged in, prevent editing name/email
    if (user) {
      if (name === "email") return;
      if (name === "name" && user.displayName !== null) return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.displayName || "",
        email: user.email || "",
      }));
    } else {
      setFormData({
        name: "",
        email: "",
        message: "",
      });
    }
  }, [user]);

  const validateEmail = (email) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, message } = formData;

    // Basic validation
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError("Please fill in all fields.");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setError("");
    setIsSending(true);

    try {
      await addDoc(collection(db, "conversations"), {
        uid: user?.uid || null,
        name,
        email,
        userType: user ? "registered" : "guest",
        createdAt: serverTimestamp(),
        messages: [
          {
            from: "user",
            message,
            timestamp: new Date(), // ✅
          },
        ],
      });
      setSubmitted(true);
      setFormData({
        name: user?.displayName || "",
        email: user?.email || "",
        message: "",
      });
    } catch (err) {
      console.error("Error saving conversation:", err);
      setError("Something went wrong. Please try again later.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | GTAFanPage – Get in Touch</title>
        <meta
          name="description"
          content="Have a question, suggestion, or partnership idea? Contact the GTAFanPage.com team. We're here to help and connect with the GTA community!"
        />
        <link rel="canonical" href="https://www.gtafanpage.com/contact" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact GTAFanPage.com" />
        <meta
          property="og:description"
          content="Reach out to the GTAFanPage.com team with your questions, feedback, or media inquiries."
        />
        <meta property="og:url" content="https://www.gtafanpage.com/contact" />
        <meta
          property="og:image"
          content="https://www.gtafanpage.com/og-image.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact GTAFanPage.com" />
        <meta
          name="twitter:description"
          content="Connect with the team behind GTAFanPage.com for support, feedback, or business opportunities."
        />
        <meta
          name="twitter:image"
          content="https://www.gtafanpage.com/og-image.jpg"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "GTAFanPage",
            url: "https://www.gtafanpage.com",
            logo: "https://www.gtafanpage.com/logo192.png",
            contactPoint: [
              {
                "@type": "ContactPoint",
                email: "support@gtafanpage.com",
                contactType: "customer support",
                areaServed: "Worldwide",
                availableLanguage: ["English"],
              },
            ],
            sameAs: [
              "https://twitter.com/GTAFanPage",
              "https://www.youtube.com/@RockstarGames",
            ],
          })}
        </script>
      </Helmet>

      <PageWrapper>
        <div className="contact-page-wrapper">
          <div className="contact-card">
            <h2>Get in Touch</h2>

            {submitted && (
              <p className="success-msg">✅ Your message has been sent!</p>
            )}
            {error && <p className="error-msg">⚠️ {error}</p>}

            <form onSubmit={handleSubmit} className="contact-form" noValidate>
              <label>
                Full Name
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={!user ? "John Doe" : ""}
                  disabled={user && user.displayName !== null}
                  required
                />
              </label>

              <label>
                Email Address
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={!user ? "you@example.com" : ""}
                  disabled={!!user}
                  required
                />
              </label>

              <label>
                Your Message
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="How can we help you?"
                  rows="5"
                  required
                ></textarea>
              </label>

              <button type="submit" disabled={isSending}>
                {isSending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </PageWrapper>
    </>
  );
};

export default Contact;
